import { useCallback } from 'react';
import { useDataFetchingClient } from '../../utils/DataFetchingClient';
import { useAssignContentMutation, useUpdateContentAssignmentMutation, useGetContentAssignmentsByIdsQuery, useDeleteContentAssignmentMutation, useSearchAggregationQuery, useSearchQuery } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/assignments/rpc/ContentAssignmentsChirpService.hooks';
import { GET_CONTENT_ASSIGNMENTS_BY_IDS_QUERY_FIELD_NAME, SEARCH_AGGREGATION_QUERY_FIELD_NAME, SEARCH_QUERY_FIELD_NAME } from 'academy-common-lib/__generated__/chirp/com/hubspot/learning/center/assignments/rpc/ContentAssignmentsChirpService.dfc.new';
import { TABLE_SIZE } from '../table/tableTypes';
import Raven from 'raven-js';
const refetchContentAssignments = client => client.refetchQueries({
  updateCache(cache) {
    cache.evict({
      fieldName: GET_CONTENT_ASSIGNMENTS_BY_IDS_QUERY_FIELD_NAME
    });
    cache.evict({
      fieldName: SEARCH_AGGREGATION_QUERY_FIELD_NAME
    });
    cache.evict({
      fieldName: SEARCH_QUERY_FIELD_NAME
    });
  }
});
export const useContentAssignmentsByIds = ({
  assignmentIds,
  skip
}) => {
  const {
    data,
    loading,
    error
  } = useGetContentAssignmentsByIdsQuery({
    skip,
    variables: {
      assignmentId: assignmentIds
    }
  });
  return {
    contentAssignments: data === null || data === void 0 ? void 0 : data.contentAssignments,
    contentAssignmentsLoading: loading,
    contentAssignmentsError: error
  };
};
export const useCreateContentAssignment = ({
  onCompleted
} = {}) => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: createContentAssignmentLoading,
    error: createContentAssignmentError
  }] = useAssignContentMutation({
    update: (_cache, {
      errors
    }) => {
      if (errors && errors.length) return;
      void refetchContentAssignments(client);
    },
    onCompleted: data => {
      var _data$result$contentA;
      const created = (_data$result$contentA = data.result.contentAssignments) !== null && _data$result$contentA !== void 0 ? _data$result$contentA : [];
      if (onCompleted) onCompleted(created);
    }
  });
  const createContentAssignment = useCallback(variables => {
    return mutate({
      variables
    }).then(response => {
      if (response && response.data) {
        var _response$data$result;
        const created = (_response$data$result = response.data.result.contentAssignments) !== null && _response$data$result !== void 0 ? _response$data$result : [];
        return created;
      }
      return null;
    }).catch(err => {
      Raven.captureException(err);
      return null;
    });
  }, [mutate]);
  return {
    createContentAssignment,
    createContentAssignmentLoading,
    createContentAssignmentError
  };
};
export const useUpdateContentAssignment = () => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: updateContentAssignmentLoading,
    error: updateContentAssignmentError
  }] = useUpdateContentAssignmentMutation({
    update: (_cache, {
      errors
    }) => {
      if (errors && errors.length) return;
      void refetchContentAssignments(client);
    }
  });
  const updateContentAssignment = useCallback(variables => {
    return mutate({
      variables: {
        assignmentIds: variables.assignmentIds,
        dueDate: variables.dueDate
      }
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate]);
  return {
    updateContentAssignment,
    updateContentAssignmentLoading,
    updateContentAssignmentError
  };
};
export const useDeleteContentAssignment = ({
  onCompleted
} = {}) => {
  const client = useDataFetchingClient();
  const [mutate, {
    loading: deleteContentAssignmentLoading,
    error: deleteContentAssignmentError
  }] = useDeleteContentAssignmentMutation({
    update: (_cache, {
      errors
    }) => {
      if (errors && errors.length) return;
      void refetchContentAssignments(client);
    }
  });
  const deleteContentAssignment = useCallback(assignmentId => {
    return mutate({
      variables: {
        assignmentId
      }
    }).then(() => {
      if (onCompleted) onCompleted();
    }).catch(error => {
      Raven.captureException(error);
    });
  }, [mutate, onCompleted]);
  return {
    deleteContentAssignment,
    deleteContentAssignmentLoading,
    deleteContentAssignmentError
  };
};
export const useContentAssignmentsPortals = ({
  contentId,
  contentType,
  offset = 0,
  limit = TABLE_SIZE,
  skip = false,
  query,
  assignedByRequestingUser = false
}) => {
  const {
    data,
    loading,
    error
  } = useSearchAggregationQuery({
    skip,
    variables: {
      contentId,
      contentType,
      query,
      assignedByRequestingUser,
      pageLength: limit,
      offset: {
        cursor: offset
      }
    }
  });
  return {
    total: data ? data.total : 0,
    contentAssignmentsPortals: data ? data.results : null,
    contentAssignmentsPortalsLoading: loading,
    contentAssignmentsPortalsError: error
  };
};
export const useContentAssignmentsForPortal = ({
  contentId,
  contentType,
  portalId,
  offset = 0,
  limit = TABLE_SIZE,
  skip = false,
  query,
  assignedByRequestingUser = false
}) => {
  const {
    data,
    loading,
    error
  } = useSearchQuery({
    skip,
    variables: {
      contentId,
      contentType,
      targetPortalId: portalId,
      query,
      assignedByRequestingUser,
      pageLength: limit,
      offset: {
        cursor: offset
      }
    }
  });
  return {
    total: data ? data.total : 0,
    contentAssignmentsForPortal: data ? data.results : null,
    contentAssignmentsForPortalLoading: loading,
    contentAssignmentsForPortalError: error
  };
};